<template>
  <div class="flex flex-col">
    <NForm ref="formRef" :model="formValues">
      <NFormItem
        :label="t('canvasUI.canvasBuilder.iframe.form.source')"
        path="src"
        :rule="{
          validator: urlValidator,
          trigger: ['input', 'blur'],
        }"
      >
        <NInput v-model:value.trim="formValues.src" clearable placeholder="" :show-button="false" />
      </NFormItem>

      <NFormItem :label="t('canvasUI.canvasBuilder.iframe.form.width')">
        <NInput v-model:value.trim="formValues.width" clearable placeholder="" :show-button="false" />
      </NFormItem>

      <NFormItem :label="t('canvasUI.canvasBuilder.iframe.form.height')">
        <NInput v-model:value.trim="formValues.height" clearable placeholder="" :show-button="false" />
      </NFormItem>

      <NFormItem :show-feedback="false" :show-label="false">
        <NCheckbox v-model:checked="formValues.add_session_context">
          {{ t('canvasUI.canvasBuilder.iframe.form.addSessionContext') }}
        </NCheckbox>
      </NFormItem>

      <NFormItem
        v-if="isFullScreenModeEnabled && !isScribbleChild"
        :label="t('canvasUI.canvasBuilder.iframe.form.fullScreenMode')"
        :show-feedback="false"
      >
        <NCheckbox v-model:checked="formValues.fullScreenMode">
          {{ t('canvasUI.canvasBuilder.iframe.form.fullScreenModeDescription') }}
        </NCheckbox>
      </NFormItem>
    </NForm>
    <div
      class="absolute bottom-0 left-0 z-2 mx-8 py-4"
      :style="{
        width: 'calc(100% - 2 * var(--n-body-padding))',
        backgroundColor: themeVars.base,
      }"
    >
      <CButton block class="mt-auto" :disabled="!formValues.src" type="primary" @click="onSave">
        {{ t('canvasUI.common.done') }}
      </CButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, toRef, inject, type ComputedRef } from 'vue'
import { NForm, NFormItem, NInput, NCheckbox, type FormInst, type FormItemRule } from 'naive-ui'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import type { IFrameProps } from '@canvas-builder/types/canvas'
import { ComponentTypes } from '@canvas-builder/types/canvas'
import { useFetchApps } from '@lib/api/apps/apps.queries'
import { loadCanvasAndDsrApps } from '@lib/composables/useInstalledApps'
import useThemeVars from '@lib/composables/useThemeVars'
import { enforceHttps } from '@canvas-builder/util/url'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const props = defineProps<{
  id: string
  data: IFrameProps
}>()

const themeVars = useThemeVars()
const { t } = useI18n()

// Inject LaunchDarkly flags
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { updateNodeDataById, setComponentEditMode, getParentType } = useCanvas()
const { isFetching: isFetchingApps } = useFetchApps(loadCanvasAndDsrApps)
const formRef = ref<FormInst | null>(null)

const formValues = reactive({
  width: props.data.width,
  height: props.data.height,
  src: props.data.src,
  fullScreenMode: props.data.full_screen_mode ?? false,
  add_session_context: props.data.add_session_context,
})

const parentType = getParentType(toRef(props.id))
const isScribbleChild = computed(() => {
  return parentType.value === ComponentTypes.Scribble
})

// Check if full screen mode feature is enabled
const isFullScreenModeEnabled = computed(() => launchDarkly.value?.enable_embeddable_full_screen_mode ?? false)

function urlValidator(_: FormItemRule, v: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!v) return reject(new Error(t('canvasUI.canvasBuilder.iframe.form.errors.requiredUrl')))
    return resolve()
  })
}

async function onSave() {
  formRef.value?.validate((errors) => {
    if (!errors) {
      updateNodeDataById(props.id, {
        width: formValues.width,
        height: formValues.height,
        src: enforceHttps(formValues.src!),
        full_screen_mode: formValues.fullScreenMode,
        add_session_context: formValues.add_session_context,
      } satisfies IFrameProps)
      setComponentEditMode(false)
    }
  })
}

defineExpose({
  isBusy: isFetchingApps,
})
</script>
